// src/utils/legal-api.js — REST helpers for the legal-document agents (/api/legal).
// Same Firebase-token pattern as outreach-api.js. See AUGUST_CLONE_PLAN.md.
import { auth } from '../firebase/firebaseConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const idToken = await user.getIdToken();
  return { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' };
}

async function request(path, options = {}) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/legal${path}`, { ...options, headers });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { error: text }; }
  if (!res.ok) {
    const msg = (data && (data.error || data.detail)) || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return data;
}

// Read a File into base64 (strips the "data:...;base64," prefix). Used for PDF upload.
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Ask a question about one contract. Pass either documentText OR documentBase64 (+mediaType).
export function askDocument({ documentText, documentBase64, mediaType, documentTitle, question }) {
  return request('/ask', {
    method: 'POST',
    body: JSON.stringify({ documentText, documentBase64, mediaType, documentTitle, question }),
  });
}

// Saved Tabular Review column templates.
export function listTabularTemplates() {
  return request('/tabular-templates', { method: 'GET' });
}
export function createTabularTemplate({ name, columns }) {
  return request('/tabular-templates', { method: 'POST', body: JSON.stringify({ name, columns }) });
}
export function deleteTabularTemplate(id) {
  return request(`/tabular-templates/${id}`, { method: 'DELETE' });
}

// Compare clauses across contracts. documents: [{ title, text } | { title, base64, mediaType }]
export function tabularReview({ documents, columns, fields }) {
  return request('/tabular-review', {
    method: 'POST',
    body: JSON.stringify({ documents, columns, fields }),
  });
}

// Run diligence across a deal's documents → findings + checklist, each serious finding verified.
export function runDiligence({ documents, dealContext, verify }) {
  return request('/diligence', {
    method: 'POST',
    body: JSON.stringify({ documents, dealContext, verify }),
  });
}

// Word-level redline diff between two clause/contract versions.
export function diffVersions({ before, after }) {
  return request('/diff', { method: 'POST', body: JSON.stringify({ before, after }) });
}

// Draft a new clause or redline an existing one, optionally anchored to firm precedent.
export function draftClause({ instruction, existingClause, precedent, governingLaw, party }) {
  return request('/draft-clause', {
    method: 'POST',
    body: JSON.stringify({ instruction, existingClause, precedent, governingLaw, party }),
  });
}

// Orchestrator: classify a free-text request and dispatch to the right specialist agent.
export function assistantRequest({ request: userRequest, documents }) {
  return request('/assistant', {
    method: 'POST',
    body: JSON.stringify({ request: userRequest, documents }),
  });
}

// Agent mode: plan + run a multi-step task, streaming progress via Server-Sent Events.
// onEvent receives each parsed event ({type:'plan'|'step'|'done'|'error', ...}).
// Returns a promise that resolves when the stream closes. EventSource can't set the auth
// header, so we read the body stream manually.
export async function streamAgentRun({ request: userRequest, documents }, onEvent) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/legal/agent`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ request: userRequest, documents }),
  });
  if (!res.ok || !res.body) {
    let detail = `HTTP ${res.status}`;
    try { const j = await res.json(); detail = j.error || j.detail || detail; } catch { /* noop */ }
    throw new Error(detail);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() || ''; // keep the trailing partial block
    for (const block of blocks) {
      for (const line of block.split('\n')) {
        if (!line.startsWith('data:')) continue; // skip ": ping" comments
        const payload = line.slice(5).trim();
        if (!payload) continue;
        try { onEvent(JSON.parse(payload)); } catch { /* ignore malformed */ }
      }
    }
  }
}

// Extract a structured term sheet from one contract.
export function extractKeyTerms({ documentText, documentBase64, mediaType }) {
  return request('/key-terms', {
    method: 'POST',
    body: JSON.stringify({ documentText, documentBase64, mediaType }),
  });
}

// Extract obligations + key dates (a "contract calendar") from one contract.
export function extractObligations({ documentText, documentBase64, mediaType }) {
  return request('/obligations', {
    method: 'POST',
    body: JSON.stringify({ documentText, documentBase64, mediaType }),
  });
}

// Build an .ics contract calendar from extracted obligations (returns raw iCalendar text).
export async function obligationsICS(obligations) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/legal/obligations-ics`, {
    method: 'POST', headers, body: JSON.stringify({ obligations }),
  });
  if (!res.ok) throw new Error('Failed to build calendar');
  return res.text();
}

// Compose a client-ready memo from findings/notes/diligence output.
export function generateMemo({ matter, audience, instructions, source }) {
  return request('/memo', {
    method: 'POST',
    body: JSON.stringify({ matter, audience, instructions, source }),
  });
}

// List the built-in negotiation playbooks (for the picker).
export function listPlaybooks() {
  return request('/playbooks', { method: 'GET' });
}

// Redline a counterparty contract against a playbook. Provide ONE of:
//   playbookId (a saved playbook), or playbook (a built-in key / custom { party, rules }).
export function playbookReview({ documentText, documentBase64, mediaType, documentTitle, playbookId, playbook, dealContext }) {
  return request('/playbook-review', {
    method: 'POST',
    body: JSON.stringify({ documentText, documentBase64, mediaType, documentTitle, playbookId, playbook, dealContext }),
  });
}

// --- Saved (custom) playbooks — the "Deploy" half of Configure -------------
export function listMyPlaybooks() {
  return request('/my-playbooks', { method: 'GET' });
}
export function createMyPlaybook({ name, party, description, rules, basedOn }) {
  return request('/my-playbooks', { method: 'POST', body: JSON.stringify({ name, party, description, rules, basedOn }) });
}
export function updateMyPlaybook(id, { name, party, description, rules }) {
  return request(`/my-playbooks/${id}`, { method: 'PUT', body: JSON.stringify({ name, party, description, rules }) });
}
export function deleteMyPlaybook(id) {
  return request(`/my-playbooks/${id}`, { method: 'DELETE' });
}

// --- Folders: per-matter document store ------------------------------------
export function listFolders() {
  return request('/folders', { method: 'GET' });
}
export function createFolder({ name, description }) {
  return request('/folders', { method: 'POST', body: JSON.stringify({ name, description }) });
}
export function deleteFolder(id) {
  return request(`/folders/${id}`, { method: 'DELETE' });
}
export function listFolderDocuments(id) {
  return request(`/folders/${id}/documents`, { method: 'GET' });
}
export function addFolderDocument(id, { title, text, base64, mediaType }) {
  return request(`/folders/${id}/documents`, { method: 'POST', body: JSON.stringify({ title, text, base64, mediaType }) });
}
export function deleteFolderDocument(id, docId) {
  return request(`/folders/${id}/documents/${docId}`, { method: 'DELETE' });
}

// --- Workflows: saved, reusable Agent tasks --------------------------------
export function listWorkflows() {
  return request('/workflows', { method: 'GET' });
}
export function createWorkflow({ name, description, instruction }) {
  return request('/workflows', { method: 'POST', body: JSON.stringify({ name, description, instruction }) });
}
export function deleteWorkflow(id) {
  return request(`/workflows/${id}`, { method: 'DELETE' });
}

// --- Recents: activity log -------------------------------------------------
export function listActivity() {
  return request('/activity', { method: 'GET' });
}
// Fire-and-forget: log a completed run. Never throws (Recents is best-effort).
export function logActivity({ kind, title, summary }) {
  return request('/activity', { method: 'POST', body: JSON.stringify({ kind, title, summary }) }).catch(() => null);
}
export function clearActivity() {
  return request('/activity', { method: 'DELETE' });
}

// --- DMS: Nextcloud connector (open-source, secure SharePoint alternative) --
export function dmsStatus() {
  return request('/dms/status', { method: 'GET' });
}
export function dmsConnect({ baseUrl, username, appPassword, rootPath }) {
  return request('/dms/connect', { method: 'POST', body: JSON.stringify({ baseUrl, username, appPassword, rootPath }) });
}
export function dmsDisconnect() {
  return request('/dms', { method: 'DELETE' });
}
export function dmsList(path) {
  return request(`/dms/list?path=${encodeURIComponent(path || '')}`, { method: 'GET' });
}
export function dmsImport({ path, folderId }) {
  return request('/dms/import', { method: 'POST', body: JSON.stringify({ path, folderId }) });
}

// --- Matters: open matter/case registry (our SOS replacement) --------------
export function listMatters() {
  return request('/matters', { method: 'GET' });
}
export function createMatter(body) {
  return request('/matters', { method: 'POST', body: JSON.stringify(body) });
}
export function updateMatter(id, body) {
  return request(`/matters/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}
export function deleteMatter(id) {
  return request(`/matters/${id}`, { method: 'DELETE' });
}
export function runMatterDiligence(id) {
  return request(`/matters/${id}/run-diligence`, { method: 'POST', body: '{}' });
}
export function buildMatterCalendar(id) {
  return request(`/matters/${id}/build-calendar`, { method: 'POST', body: '{}' });
}
export async function matterCalendarICS(id) {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}/api/legal/matters/${id}/calendar.ics`, { headers });
  if (!res.ok) throw new Error('Failed to build calendar');
  return res.text();
}
// Compose a client-ready memo straight from the matter's synced state
// (diligence summary + closing checklist + obligations). audience/instructions optional.
export function matterMemo(id, { audience, instructions } = {}) {
  return request(`/matters/${id}/memo`, { method: 'POST', body: JSON.stringify({ audience, instructions }) });
}

// --- Firm profile: the per-firm Configure mechanic -------------------------
export function getFirmProfile() {
  return request('/firm-profile', { method: 'GET' });
}
export function saveFirmProfile(profile) {
  return request('/firm-profile', { method: 'PUT', body: JSON.stringify(profile) });
}

// --- Integration API keys (long-lived auth for the add-ins/connectors) -----
export function listApiKeys() {
  return request('/keys', { method: 'GET' });
}
export function createApiKey(name) {
  return request('/keys', { method: 'POST', body: JSON.stringify({ name }) });
}
export function deleteApiKey(id) {
  return request(`/keys/${id}`, { method: 'DELETE' });
}

export { API_BASE_URL };
